/**
 * Multicolumn flip cards — click card face or plus/close to rotate (Y-axis).
 */
(function () {
  const FLIPPED_CLASS = 'is-flipped';
  const IGNORE_FLIP_SELECTOR =
    'a[href], button, .wt-collapse__trigger, .wt-collapse__target, input, select, textarea, label';
  const SCROLL_DRAG_THRESHOLD = 8;
  let backScrollDrag = false;

  function getBackInner(card) {
    return card.querySelector('.wt-multicol-flip-card__back-inner');
  }

  function isScrollable(inner) {
    return Boolean(inner && inner.scrollHeight > inner.clientHeight + 1);
  }

  function lockSceneHeight(card, flipped) {
    const scene = card.querySelector('.wt-multicol-flip-card__scene');
    const front = card.querySelector('.wt-multicol-flip-card__face--front');
    if (!scene || !front) return;

    if (flipped) {
      scene.style.height = `${front.offsetHeight}px`;
    } else {
      scene.style.height = '';
    }
  }

  function scrollBackInner(inner, deltaY) {
    const maxScroll = inner.scrollHeight - inner.clientHeight;
    inner.scrollTop = Math.max(0, Math.min(maxScroll, inner.scrollTop + deltaY));
  }

  function getBackInnerFromEvent(event) {
    if (event.target.closest('[data-flip-toggle]')) return null;
    return event.target.closest('.wt-multicol-flip-card__back-inner');
  }

  function canConsumeScroll(inner, deltaY) {
    const atTop = inner.scrollTop <= 0;
    const atBottom = inner.scrollTop + inner.clientHeight >= inner.scrollHeight - 1;
    if (deltaY > 0 && atBottom) return false;
    if (deltaY < 0 && atTop) return false;
    return true;
  }

  function handleBackInnerWheel(event) {
    const inner = getBackInnerFromEvent(event);
    if (!inner) return;

    const card = inner.closest('.wt-multicol-flip-card');
    if (!card?.classList.contains(FLIPPED_CLASS)) return;
    if (!isScrollable(inner)) return;
    if (!canConsumeScroll(inner, event.deltaY)) return;

    event.preventDefault();
    event.stopPropagation();
    scrollBackInner(inner, event.deltaY);
  }

  function handleBackInnerTouchMove(event) {
    const inner = getBackInnerFromEvent(event);
    if (!inner) return;

    const card = inner.closest('.wt-multicol-flip-card');
    if (!card?.classList.contains(FLIPPED_CLASS)) return;
    if (!isScrollable(inner)) return;

    const touch = event.touches[0];
    const lastY = Number(inner.dataset.touchScrollLastY);
    if (!touch || Number.isNaN(lastY)) return;

    const deltaY = lastY - touch.clientY;
    inner.dataset.touchScrollLastY = String(touch.clientY);

    if (!canConsumeScroll(inner, deltaY)) return;

    event.preventDefault();
    event.stopPropagation();
    inner.scrollTop += deltaY;
  }

  function handleBackInnerTouchStart(event) {
    const inner = getBackInnerFromEvent(event);
    if (!inner) return;

    const touch = event.touches[0];
    if (!touch) return;

    inner.dataset.touchScrollLastY = String(touch.clientY);
  }

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (!event.target.closest('.wt-multicol-flip-card__back-inner')) return;
      backScrollDrag = false;
      const startX = event.clientX;
      const startY = event.clientY;

      const onPointerMove = (moveEvent) => {
        if (
          Math.abs(moveEvent.clientX - startX) > SCROLL_DRAG_THRESHOLD ||
          Math.abs(moveEvent.clientY - startY) > SCROLL_DRAG_THRESHOLD
        ) {
          backScrollDrag = true;
        }
      };

      const onPointerEnd = () => {
        document.removeEventListener('pointermove', onPointerMove);
        document.removeEventListener('pointerup', onPointerEnd);
        document.removeEventListener('pointercancel', onPointerEnd);
      };

      document.addEventListener('pointermove', onPointerMove);
      document.addEventListener('pointerup', onPointerEnd);
      document.addEventListener('pointercancel', onPointerEnd);
    },
    true
  );

  function closeBackAccordions(card) {
    card.querySelectorAll('.wt-multicol-flip-card__face--back collapsible-section').forEach((section) => {
      section.dataset.open = 'false';
      const trigger = section.querySelector('.wt-collapse__trigger');
      if (trigger) {
        trigger.classList.remove('wt-collapse__trigger--active');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.dataset.open = 'false';
      }
    });
  }

  function setFlipped(card, flipped) {
    card.classList.toggle(FLIPPED_CLASS, flipped);
    card.querySelectorAll('[data-flip-toggle]').forEach((btn) => {
      btn.setAttribute('aria-expanded', flipped ? 'true' : 'false');
    });
    const back = card.querySelector('.wt-multicol-flip-card__face--back');
    if (back) {
      back.setAttribute('aria-hidden', flipped ? 'false' : 'true');
    }
    closeBackAccordions(card);
    lockSceneHeight(card, flipped);

    const inner = getBackInner(card);
    if (flipped && inner) {
      inner.scrollTop = 0;
    }
  }

  function toggleCard(card) {
    if (!card.querySelector('.wt-multicol-flip-card__face--back')) return;
    setFlipped(card, !card.classList.contains(FLIPPED_CLASS));
  }

  function shouldIgnoreFlipClick(target) {
    return Boolean(target.closest(IGNORE_FLIP_SELECTOR));
  }

  function onFlipClick(event) {
    const toggle = event.target.closest('[data-flip-toggle]');
    if (toggle) {
      const card = toggle.closest('.wt-multicol-flip-card');
      if (!card) return;
      event.preventDefault();
      event.stopPropagation();
      toggleCard(card);
      return;
    }

    const face = event.target.closest('[data-flip-card-face]');
    if (!face) return;

    const card = face.closest('.wt-multicol-flip-card');
    if (!card || !card.classList.contains('wt-multicol-flip-card')) return;

    if (
      face.dataset.flipCardFace === 'back' &&
      event.target.closest('.wt-multicol-flip-card__back-inner') &&
      backScrollDrag
    ) {
      backScrollDrag = false;
      return;
    }

    backScrollDrag = false;

    if (shouldIgnoreFlipClick(event.target)) return;

    event.preventDefault();
    toggleCard(card);
  }

  document.addEventListener('click', onFlipClick);
  document.addEventListener('wheel', handleBackInnerWheel, { passive: false, capture: true });
  document.addEventListener('touchstart', handleBackInnerTouchStart, { passive: true, capture: true });
  document.addEventListener('touchmove', handleBackInnerTouchMove, { passive: false, capture: true });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('.wt-multicol-flip-card.is-flipped')?.forEach((card) => {
      setFlipped(card, false);
    });
  });
})();
